import { format, parseISO } from "date-fns";
import { enUS, fr, zhCN, type Locale } from "date-fns/locale";
import { Bell, CheckCheck, Mail, MailOpen, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  selectUnreadCount,
  useNotificationStore,
  type InAppNotification,
} from "@/core/stores/notification.store";
import { getCurrentLanguage } from "@/core/utils/func/language-resolver";
import { cn } from "@/core/utils/func/cn";

const DATE_FNS_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
  zh: zhCN,
};

function formatReceivedAt(receivedAt: string): string {
  try {
    return format(parseISO(receivedAt), "Pp", {
      locale: DATE_FNS_LOCALES[getCurrentLanguage()] ?? fr,
    });
  } catch {
    return receivedAt;
  }
}

/**
 * Centre de notifications — boîte de réception locale des notifications push
 * (FCM) de tout utilisateur authentifié. Aucun appel API : tout est lu /
 * modifié dans `notification.store` (persisté localement).
 */
export function NotificationCenterPage() {
  const { t } = useTypedTranslation();
  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore(selectUnreadCount);
  const markRead = useNotificationStore((s) => s.markRead);
  const markUnread = useNotificationStore((s) => s.markUnread);
  const markAllRead = useNotificationStore((s) => s.markAllRead);
  const removeNotification = useNotificationStore((s) => s.removeNotification);
  const clearAll = useNotificationStore((s) => s.clearAll);

  const [selected, setSelected] = useState<InAppNotification | null>(null);

  const openDetail = (notification: InAppNotification) => {
    markRead(notification.id);
    setSelected({ ...notification, read: true });
  };

  const selectedFresh = selected
    ? (notifications.find((n) => n.id === selected.id) ?? selected)
    : null;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {t("notifications.center.title")}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t("notifications.center.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            <CheckCheck className="mr-2 h-4 w-4" />
            {t("notifications.center.actions.markAllRead")}
          </Button>
          <Button
            variant="outline"
            onClick={clearAll}
            disabled={notifications.length === 0}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("notifications.center.actions.clearAll")}
          </Button>
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Bell className="size-10" />
            <p className="max-w-md text-center text-sm">
              {t("notifications.center.empty")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              role="button"
              tabIndex={0}
              className={cn(
                "cursor-pointer transition-colors hover:bg-muted/50",
                !notification.read && "border-primary/40"
              )}
              onClick={() => openDetail(notification)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openDetail(notification);
                }
              }}
            >
              <CardContent className="flex items-start gap-3 p-4">
                <span
                  className={cn(
                    "mt-2 size-2 shrink-0 rounded-full",
                    notification.read ? "bg-transparent" : "bg-primary"
                  )}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <NoTranslate
                    as="span"
                    className={cn(
                      "line-clamp-1 text-sm",
                      notification.read
                        ? "font-normal text-muted-foreground"
                        : "font-semibold"
                    )}
                  >
                    {notification.title}
                  </NoTranslate>
                  <NoTranslate
                    as="span"
                    className="line-clamp-2 text-sm text-muted-foreground"
                  >
                    {notification.body}
                  </NoTranslate>
                  <span className="text-xs text-muted-foreground">
                    {formatReceivedAt(notification.receivedAt)}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label={
                      notification.read
                        ? t("notifications.center.actions.markUnread")
                        : t("notifications.center.actions.markRead")
                    }
                    title={
                      notification.read
                        ? t("notifications.center.actions.markUnread")
                        : t("notifications.center.actions.markRead")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      notification.read
                        ? markUnread(notification.id)
                        : markRead(notification.id);
                    }}
                  >
                    {notification.read ? (
                      <Mail className="size-4" />
                    ) : (
                      <MailOpen className="size-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-destructive hover:text-destructive"
                    aria-label={t("notifications.center.actions.delete")}
                    title={t("notifications.center.actions.delete")}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notification.id);
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={selectedFresh !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("notifications.center.detail.title")}</DialogTitle>
          </DialogHeader>
          {selectedFresh && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <NoTranslate as="span" className="text-base font-semibold">
                  {selectedFresh.title}
                </NoTranslate>
                <NoTranslate
                  as="span"
                  className="block whitespace-pre-wrap text-sm text-muted-foreground"
                >
                  {selectedFresh.body}
                </NoTranslate>
              </div>
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">
                  {t("notifications.center.detail.receivedAt")}
                </span>
                <span>{formatReceivedAt(selectedFresh.receivedAt)}</span>
              </div>
              {selectedFresh.data &&
                Object.keys(selectedFresh.data).length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">
                      {t("notifications.center.detail.payload")}
                    </span>
                    <div className="flex flex-col gap-1.5 rounded-md border bg-muted/40 p-3">
                      {Object.entries(selectedFresh.data).map(
                        ([key, value]) => (
                          <div
                            key={key}
                            className="flex items-start justify-between gap-4 text-sm"
                          >
                            <span className="shrink-0 text-muted-foreground">
                              {key}
                            </span>
                            <NoTranslate
                              as="span"
                              className="break-all text-right font-medium"
                            >
                              {value}
                            </NoTranslate>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
          <DialogFooter>
            {selectedFresh && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    selectedFresh.read
                      ? markUnread(selectedFresh.id)
                      : markRead(selectedFresh.id);
                  }}
                >
                  {selectedFresh.read
                    ? t("notifications.center.actions.markUnread")
                    : t("notifications.center.actions.markRead")}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    removeNotification(selectedFresh.id);
                    setSelected(null);
                  }}
                >
                  {t("notifications.center.actions.delete")}
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => setSelected(null)}>
              {t("notifications.center.detail.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
